/* eslint class-methods-use-this: 0, no-param-reassign: 0 */
import { AdBridgeAgentStates, AdBridgeAgentTypes } from 'client/util/Constants';
import AdBridgeAgentApi, { AdBridgeAgentApiClass } from 'client/api/v2/AdBridgeAgentApi';
import CollectionModule from 'client/store/modules/CollectionModule';
import IdsourceApi from 'client/api/v2/IdsourceApi';
import Vue from 'vue';
import moment from 'moment';
import semver from 'semver';

export class AdBridgeAgentCollectionClass extends CollectionModule {
  extractModule() {
    const module = super.extractModule();

    module.actions.activate = (...params) => this.activate(...params);
    module.actions.deactivate = (...params) => this.deactivate(...params);
    module.actions.delete = (...params) => this.delete(...params);
    module.actions.setAdBridgeDomain = (...params) => this.setAdBridgeDomain(...params);

    module.getters.isUpdateAvailable = (...params) => this.isUpdateAvailable(...params);
    module.getters.isContactOverdue = (...params) => this.isContactOverdue(...params);

    module.mutations.SET_AD_BRIDGE_DOMAIN = (...params) => this.SET_AD_BRIDGE_DOMAIN(...params);

    return module;
  }

  // --------------------------------------------------------------------------
  // State
  // --------------------------------------------------------------------------

  getInitialState() {
    const state = super.getInitialState();

    state.features.id = '';
    state.features.domain = '';

    return state;
  }

  // --------------------------------------------------------------------------
  // Actions
  // --------------------------------------------------------------------------

  activate({ commit }, id) {
    const attributes = { active: true };
    const item = { _id: id, ...attributes };

    return IdsourceApi
      .update(item)
      .then(() => commit('UPDATE_ITEM', {
        id,
        attributes: { state: AdBridgeAgentStates.active },
      }));
  }

  deactivate({ commit }, id) {
    const attributes = { active: false };
    const item = { _id: id, ...attributes };

    return IdsourceApi
      .update(item)
      .then(() => commit('UPDATE_ITEM', {
        id,
        attributes: { state: AdBridgeAgentStates.inactive },
      }));
  }

  delete({ commit, state }, id) {
    const { importAgent } = AdBridgeAgentTypes;
    const item = state.entities.items.byId[id];
    const { type } = item;

    const api = type === importAgent ? IdsourceApi : new AdBridgeAgentApiClass(state.features.id);

    return api
      .delete(id)
      .then(() => commit('DELETE_ITEMS', [id]));
  }

  fetchCollection({ commit, state }, { next = false } = {}) {
    // All AD Bridge agents will initially be fetched. So ignore later requests
    // that may be triggered by scrolling.
    if (next === true) {
      return Promise.resolve();
    }

    const { domain, id } = state.features;
    const { sort } = state.features.items;

    return AdBridgeAgentApi
      .fetchAll(domain, id, sort)
      .then((items) => {
        const payload = {
          results: items,
          totalCount: items.length,
          totalCountOfAllItems: items.length,
        };

        commit('SET_COLLECTION', payload);

        return items.map(result => result.id);
      });
  }

  setAdBridgeDomain({ commit }, { domain, id }) {
    commit('SET_AD_BRIDGE_DOMAIN', { domain, id });
  }

  // --------------------------------------------------------------------------
  // Getters
  // --------------------------------------------------------------------------

  isUpdateAvailable(state, getters, rootState) {
    return (agent) => {
      const { importAgent } = AdBridgeAgentTypes;

      const latestAgentVersion = agent.type === importAgent
        ? rootState.SettingsModule.directoryAgentVersion
        : rootState.SettingsModule.syncAgentVersion;

      if (latestAgentVersion && agent.version) {
        return semver.lt(agent.version, latestAgentVersion);
      }

      return false;
    };
  }

  isContactOverdue(state = { entities: { items: { byId: {} } } }) {
    return (id) => {
      if (!state.entities.items.byId[id]) {
        return undefined;
      }

      const { lastContact } = state.entities.items.byId[id];
      const lastContact2 = moment(lastContact).utc();

      return moment().utc().diff(lastContact2) > 15 * 60 * 1000;
    };
  }

  // --------------------------------------------------------------------------
  // Mutations
  // --------------------------------------------------------------------------

  RESET_COLLECTION(state, payload = {}) {
    super.RESET_COLLECTION(state, payload);

    if (!payload.soft) {
      const initialState = this.getInitialState();
      state.features.id = initialState.features.id;
      state.features.domain = initialState.features.domain;
      Vue.set(state.features.items, 'filters', initialState.features.items.filters);
    }
  }

  SET_AD_BRIDGE_DOMAIN(state, { domain, id }) {
    state.features.id = id;
    state.features.domain = domain;
  }
}

export default new AdBridgeAgentCollectionClass(AdBridgeAgentApi, 'id', 'hostname').extractModule();
